import { useEffect, useState } from "react";
import type { ReactNode } from "react";

type Mode = "light" | "system" | "dark";
const KEY = "fatfire-theme";
/** Fired when one toggle changes the theme, so the other copy on the page follows. */
const EVENT = "fatfire-theme-change";

function readMode(): Mode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    // Storage can be blocked; fall back to the system setting.
  }
  return "system";
}

const ICONS: Record<Mode, ReactNode> = {
  light: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  ),
};

const OPTIONS: { id: Mode; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "system", label: "Match system" },
  { id: "dark", label: "Dark" },
];

/** Light / system / dark switch. The choice is kept in localStorage and applied
 *  before first paint by public/theme-init.js. */
export function ThemeToggle({ className = "" }: { className?: string }) {
  const [mode, setModeState] = useState<Mode>(readMode);

  useEffect(() => {
    const follow = (e: Event) => setModeState((e as CustomEvent<Mode>).detail);
    window.addEventListener(EVENT, follow);
    return () => window.removeEventListener(EVENT, follow);
  }, []);

  const setMode = (m: Mode) => {
    setModeState(m);
    window.dispatchEvent(new CustomEvent<Mode>(EVENT, { detail: m }));
  };

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", mode);
    try {
      if (mode === "system") localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, mode);
    } catch {
      // Not persisted; the theme still applies for this visit.
    }
  }, [mode]);

  return (
    <div className={`theme-toggle ${className}`.trim()} role="group" aria-label="Colour theme">
      {OPTIONS.map((o) => (
        <button
          key={o.id}
          type="button"
          title={o.label}
          aria-pressed={mode === o.id}
          onClick={() => setMode(o.id)}
        >
          {ICONS[o.id]}
          <span className="sr-only">{o.label}</span>
        </button>
      ))}
    </div>
  );
}
